import React, { useEffect, useState, useMemo, memo, useCallback, useRef, useLayoutEffect } from 'react';
import { connect } from 'react-redux';
import cn from 'classnames';
import { bindActionCreators } from 'redux';
import { TouchableButton } from '@components/common/Touchable';
import getDeviceType from '@utility/deviceType';
import {
  changeCheckOrdersTab,
  deleteItm,
  updateSeatInfo,
  replaceCart,
  removeItemFromCart,
  getUpdatedCart,
  setHighlightCartItems,
  singleSend,
  pushModifiersGroupData,
  setUpdatingPosCartStatus,
} from '@reducers/orders';
import { handleRepeatData } from '../MenuCartBarHandler';
import {
  returnSentItems,
  pendingItemList,
  styleGenerator,
  pendingCartButton,
  sentCartButton,
  getLinkedMenuItemsId,
  activateGameCards,
  refillButtonVisibilityCheck,
  itemVoidButtonVisibilityCheck,
  repeatButtonVisibilityCheck,
  getButtonVisibility,
  displayKdsStatus,
  getLocalTime,
} from './CartHelper';
import styles from './CartItems.module.scss';
import { CUSTOM_MESSAGES, LABEL } from '@veriables';
import CartItemsTab from './CartItemsTab';
import ActionConfirmationModal from '../Modals/ActionConfirmationModal';
import MoveItemModal from '../Modals/MoveItemModal';
import { updateChecksAfterResponse } from '@reducers/storeChecks';
import { getObjectValue } from '@utility/utills';
import CustomEventEmitter from '@utility/customEventEmitter';
import { setErrorPopUp } from '@reducers/notification';
import { addInProcessItemsToCart } from '../../MenuOrderHandler';
import { useNavigate } from 'react-router-dom';
import { APP_CONFIG } from '@constants';
import { safeJsonParse, getHoldTimerState, sortPendingCartItemsByHoldStatus } from '@utility/helperFunction';
import { DELETE, SEND } from './constants';
import { HoldTimerIcon } from '@assets/images/HoldIcons/holdTimer.jsx';
import { ReactComponent as RefillIcon } from '@assets/images/RefillIcon.svg';
import NotesIcon from '@assets/images/NotesIcon';
import HoldExpiredMessage from '@components/checksCommon/HoldExpiredMessage';
import ModifiersList from './ModifiersList';
import CartItemsSkeleton from './CartItemsSkeleton';
import Loader from '@components/common/Loader/newLoaderModal';

const HOLD_RESET = { timeStr: '00:00', isPositive: true };

// Re-renders every second — only the time span.
const HoldTimerText = React.memo(({ sendDate, serverAppTheme, onFlip }) => {
  const [timerState, setTimerState] = useState(
    () => (sendDate ? getHoldTimerState(sendDate) : HOLD_RESET) || HOLD_RESET
  );
  const prevPositiveRef = useRef(timerState.isPositive);

  useEffect(() => {
    if (!sendDate) return;
    const tick = () => setTimerState(getHoldTimerState(sendDate) || HOLD_RESET);
    tick();
    let intervalId;
    const timeoutId = setTimeout(() => {
      tick();
      intervalId = setInterval(tick, 1000);
    }, 1000 - (Date.now() % 1000));
    return () => { clearTimeout(timeoutId); clearInterval(intervalId); };
  }, [sendDate]);

  useEffect(() => {
    if (timerState.isPositive !== prevPositiveRef.current) {
      prevPositiveRef.current = timerState.isPositive;
      onFlip && onFlip(timerState.isPositive);
    }
  }, [timerState.isPositive, onFlip]);

  const { timeStr } = timerState;
  const color = serverAppTheme?.primary_text || '#000';
  return <span className="fs-12 fw-600 pl-1" style={{ color }}>{timeStr}</span>;
});

// Icon only re-renders when the timer crosses zero (positive ↔ negative), not every second.
const HoldTimerBadge = React.memo(({ sendDate, serverAppTheme, onFlip }) => {
  return (
    <div className={styles.holdTimerBadge}>
      <HoldTimerIcon width={14} height={14} fill={serverAppTheme?.primary_text || '#000'} />
      <HoldTimerText sendDate={sendDate} serverAppTheme={serverAppTheme} onFlip={onFlip} />
    </div>
  );
});

const combinePackItems = items => {
  if (!items || items.length === 0) return items;
  const qtySums = new Map();
  items.forEach(item => {
    const id = safeJsonParse(item.modifierJson, {})?.internalId;
    if (id) qtySums.set(id, (qtySums.get(id) || 0) + (item.quantity || 0));
  });
  const seen = new Set();
  return items.reduce((result, item) => {
    const id = safeJsonParse(item.modifierJson, {})?.internalId;
    if (id) {
      if (!seen.has(id)) {
        seen.add(id);
        result.push({ ...item, quantity: qtySums.get(id) });
      }
    } else {
      result.push(item);
    }
    return result;
  }, []);
};

const CartItems = props => {
  const {
    cart,
    selectedItems,
    setSelectedItems,
    deleteItm,
    deviceInfo,
    userInfo,
    replaceCart,
    allMenusHashTable,
    menuItemsHashTable,
    removeItemFromCart,
    setErrorPopUp,
    auth,
    singleSend,
    menusList,
    menuCard,
    menuIndex,
    modifierGroupsHashTable,
    pushModifiersGroupData,
    handleManagerAuthentication,
    isTogo,
    updateChecksAfterResponse,
    updateSeatInfo,
    setUpdatingPosCartStatus,
    updatingCartStatus,
    updatingPosCartStatus,
    enableItemHold,
  } = props;
  const navigate = useNavigate();
  const {
    isCounter,
    isCallCenter,
    isSpeedOrderMode,
    isDriveThru,
  } = getDeviceType();

  const counterCartButton = useMemo(
    () => [
      {
        key: 'edit',
        text: LABEL().EDIT,
        backgroundColor: 'rgb(116, 164, 255)',
      },
      {
        key: 'delete',
        text: LABEL().DELETE,
        backgroundColor: 'rgb(116, 164, 255)',
      },
    ],
    []
  );

  const sentItems = useMemo(
    () =>
      props.isCartLoading || props.position === null
        ? []
        : returnSentItems(props.check, props.position) ?? [],
    [props.check, props.isCartLoading, props.position]
  );

  const pendingItems = useMemo(
    () =>
      props.isCartLoading || props.position === null
        ? []
        : pendingItemList(cart, props.position, menuItemsHashTable) ?? [],
    [cart, props.isCartLoading, menuItemsHashTable, props.position]
  );

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const defaultMoveState = { isOpen: false, data: [] };
  const [isMoveModalOpen, setIsMoveModalOpen] = useState(defaultMoveState);
  const [activeCartButton, setActiveCartButton] = useState(() =>
    isCounter || isCallCenter || isSpeedOrderMode || isDriveThru
      ? counterCartButton
      : props.isPendingList
        ? pendingCartButton
        : sentCartButton
  );

  const defaultActionState = { isOpen: false, data: [], type: undefined };
  const [isActionModalOpen, setIsActionModalOpen] = useState(
    defaultActionState
  );
  const displayPrice = !!auth?.serverAppFeatures?.displayPrice;

  const [lapsedHolds, setLapsedHolds] = useState({});

  const handleHoldFlip = useCallback((id, isPositive) => {
    setLapsedHolds(prev => {
      if (prev[id] === !isPositive) return prev;
      return { ...prev, [id]: !isPositive };
    });
  }, []);

  useEffect(() => {
    if (isCounter || isCallCenter || isSpeedOrderMode || isDriveThru) {
      setActiveCartButton(counterCartButton);
    } else {
      setActiveCartButton(
        props.isPendingList ? pendingCartButton : sentCartButton
      );
    }
  }, [
    props.isPendingList,
    isCounter,
    isCallCenter,
    isSpeedOrderMode,
    isDriveThru,
  ]);

  const [activeCartItem, setActiveCartItem] = useState(null);
  useLayoutEffect(() => {
    props.changeCheckOrdersTab({
      isPendingList: pendingItems.length > 0,
    });
  }, [pendingItems]);

  useEffect(() => {
    props.setIsCheckBoxClicked(
      Object.values(selectedItems).some(value => value)
    );
  }, [selectedItems]);

  // Reset selected checkboxes back to initial (unselected) state whenever:
  useEffect(() => {
    setSelectedItems({});
  }, [cart, props.isPendingList, setSelectedItems]);

  const activeTab = props.isPendingList ? 'pending' : 'sent';
  const { serverAppTheme } = props;
  const newItemsStyle = useMemo(() => {
    return styleGenerator({ sentItems, serverAppTheme, pendingItems });
  }, [sentItems, serverAppTheme, pendingItems]);

  const noteColor = useMemo(
    () => ({
      color: serverAppTheme?.notes || serverAppTheme?.primary_text,
      paddingTop: '2px',
    }),
    [serverAppTheme]
  );

  const sendDateMap = useMemo(() => {
    const map = {};
    const checkItems = props.check?.orders?.[0]?.items || [];
    checkItems.forEach(ci => { if (ci.sendDate) map[ci.id] = ci.sendDate; });
    return map;
  }, [props.check]);

  const messageMap = useMemo(() => {
    const map = {};
    const checkItems = props.check?.orders?.[0]?.items || [];
    checkItems.forEach(ci => { if (ci.message) map[ci.id] = ci.message; });
    return map;
  }, [props.check]);

  const openSeatsCount = useMemo(
    () => props.check?.seats?.filter(s => s.open)?.length || 1,
    [props.check]
  );

  const counterItems = useMemo(
    () =>
      isCounter || isCallCenter || isSpeedOrderMode || isDriveThru
        ? (cart || []).filter(e => e.pending)
        : [],
    [isCounter, cart, isCallCenter, isSpeedOrderMode, isDriveThru]
  );

  const checkOrderItems = useMemo(
    () => props.check?.orders?.[0]?.items,
    [props.check]
  );

  const sortedPendingItems = useMemo(
    () => sortPendingCartItemsByHoldStatus(pendingItems, checkOrderItems),
    [pendingItems, checkOrderItems]
  );

  const sortedCounterItems = useMemo(
    () => sortPendingCartItemsByHoldStatus([...counterItems].reverse(), checkOrderItems),
    [counterItems, checkOrderItems]
  );

  const activeList =
    isCounter || isSpeedOrderMode || isCallCenter || isDriveThru
      ? sortedCounterItems
      : props.isPendingList
        ? sortedPendingItems
        : sentItems;

  const displayList = useMemo(
    () =>
      props.isPendingList || isCounter || isSpeedOrderMode || isCallCenter || isDriveThru
        ? combinePackItems(activeList)
        : activeList,
    [activeList, props.isPendingList, isCounter, isSpeedOrderMode, isCallCenter, isDriveThru]
  );

  const getSelectorId = item => {
    /*
      Items in pending list don't have an id.
      so we use internalId instead.
      Id is only generated once items are sent.
    */
    if (isCounter || isCallCenter || isSpeedOrderMode || isDriveThru)
      return item?.internalId;
    if (props.isPendingList) return item?.internalId;
    return item?.id ?? item?.discountId ?? item?.internalId;
  };

  const hasAnyCheckedItem = useMemo(
    () => Object.values(selectedItems).some(Boolean),
    [selectedItems]
  );

  const hasCheckedPackItem = useMemo(() => {
    return activeList
      .filter(item => selectedItems[getSelectorId(item)])
      .some(item => {
        const menuItem = menuItemsHashTable?.[item?.menuItemId];
        return menuItem?.minOrderQuantity > 0 || menuItem?.cardType === 1 || menuItem?.cardType === 2;
      });
  }, [activeList, selectedItems, menuItemsHashTable, isCounter, isCallCenter, isSpeedOrderMode, isDriveThru, props.isPendingList]);

  const handleSelectAll = ({ isChecked, isTabSwitched }) => {
    setActiveCartItem(null);

    if (isTabSwitched) {
      setSelectedItems({});
      return;
    }

    setSelectedItems(() => {
      const newSelectedItems = {};
      activeList.forEach(item => {
        newSelectedItems[getSelectorId(item)] = isChecked;
      });
      return newSelectedItems;
    });
  };

  const handleSelectOne = item => {
    setSelectedItems(prev => ({
      ...prev,
      [getSelectorId(item)]: !prev[getSelectorId(item)],
    }));
  };

  const deleteItem = useCallback(
    data => {
      const inProcessItems = data.filter(e => e.isProcessing);
      const itemsToDelete = data.filter(e => !e.isProcessing);
      if (inProcessItems?.length > 0) {
        let checkItems = getObjectValue(
          () => props.check?.orders[0]?.items,
          []
        ).reduce((a, v) => {
          const orderJsonModifier = safeJsonParse(v.orderJson, {})
            ?.modifierJson;
          const internalId =
            orderJsonModifier &&
            safeJsonParse(orderJsonModifier, {})?.internalId;
          a.push({ ...v, internalId });
          return a;
        }, []);
        inProcessItems.forEach(item => {
          if (item.hasLinkedMenuItem) {
            const linkedItems = cart.filter(
              e =>
                e.isLinked &&
                e.uniqueIdForLinkedItem &&
                e.uniqueIdForLinkedItem === item.uniqueIdForLinkedItem
            );
            linkedItems.forEach(e => {
              removeItemFromCart(e.internalId);
              checkItems = checkItems.filter(
                d => d.internalId !== e.internalId
              );
            });
          }
          checkItems = checkItems.filter(d => d.internalId !== item.internalId);
          removeItemFromCart(item.internalId);
        });
        const updatedCart = getUpdatedCart();
        CustomEventEmitter.dispatchEvent('getCartItems', updatedCart);
        const updatedCheckHeader = props.check;
        if (updatedCheckHeader?.orders?.[0]) {
          updatedCheckHeader.orders[0].items = checkItems;
          updateChecksAfterResponse({
            ...updatedCheckHeader,
          });
          updateSeatInfo({ ...updatedCheckHeader });
        }
      }
      if (itemsToDelete.length > 0) {
        return deleteSelectedItems(itemsToDelete);
      }
      return Promise.resolve();
    },
    [props.check]
  );

  const deleteSelectedItems = data => {
    const datatosend = {
      storeId: deviceInfo.storeId,
      deviceId: deviceInfo.deviceId,
      employeeId: userInfo?.id,
      checkHeaderId: props?.check?.id,
      employeeCard: userInfo?.employeeCard,
      items: getLinkedMenuItemsId({
        data,
        allMenusHashTable,
        menuItemsHashTable,
        cart,
      }),
    };
    console.log('zxcv');

    return deleteItm(datatosend)
      .then(res => {
        const CartItems = [];
        (res?.orders || []).forEach(order => {
          (order?.items || []).forEach(item => {
            // if (item.pending && item.itemType !== 'D') {
            //   const cartData = {
            //     ...safeJsonParse(
            //       safeJsonParse(item?.orderJson, {})?.modifierJson,
            //       {}
            //     ),
            //     isProcessing: false,
            //     modifierJson: safeJsonParse(item?.orderJson, {})?.modifierJson,
            //     id: item.id,
            //     checkItemId: item.checkItemId,
            //     pending: item?.pending,
            //   };
            //   const uniqueKey = cartData.internalId || cartData.id;
            //   if (!CartItems.find(c => (c.internalId || c.id) === uniqueKey)) {
            //     CartItems.push(cartData);
            //   }
            // }
          });
        });
        data.forEach(item => removeItemFromCart(item.internalId));
        const updatedCart = getUpdatedCart();
        CustomEventEmitter.dispatchEvent('getCartItems', updatedCart);
        updateChecksAfterResponse(res);
        updateSeatInfo({ ...res });
      })
      .catch(err => {
        setErrorPopUp({
          res: err.message,
        });
        throw err;
      });
  };

  const onModifyItem = useCallback(
    (data, type) => {
      const cartDisplayData = safeJsonParse(
        window.sessionStorage.getItem('cart'),
        {}
      );
      const matchedItem = cartDisplayData.find(
        item => item.menuItemId === data.menuItemId
      );
      if (matchedItem) {
        const displayStyle = matchedItem.serverAppModifierGroupDisplayStyle;
        window.sessionStorage.setItem(
          'serverAppModifierGroupDisplayStyle',
          displayStyle
        );
      }

      const seatId =
        Array.isArray(data.seatIds) && data.seatIds.length > 0
          ? data.seatIds[0]
          : '';

      const inProcessItem = cart.filter(e => e.isProcessing);
      if (inProcessItem.length > 0) {
        const cb = Cart => {
          const item = Cart.find(e => data.internalId === e.internalId);
          const itemSeatId =
            Array.isArray(item.seatIds) && item.seatIds.length > 0
              ? item.seatIds[0]
              : '';
          let refill = data?.refill || false;
          navigate(
            `${APP_CONFIG.BASE_URL}/order-item/${item.menuTypeId}/${item.menuItemId}/${item.internalId}/1?menuId=${item.menuId}&&checkItem=${item.id}&&seatId=${itemSeatId}&&type=${type}&&refill=${refill}`
          );
        };
        addInProcessItemsToCart(cb, cart);
      } else {
        setHighlightCartItems([data]);
        if (data.menuTypeId && data.menuItemId) {
          let refill = data?.refill || false;
          navigate(
            `${APP_CONFIG.BASE_URL}/order-item/${data.menuTypeId}/${data.menuItemId}/${data.internalId}/1?menuId=${data.menuId}&&checkItem=${data.id}&&seatId=${seatId}&&type=${type}&&refill=${refill}`
          );
        }
      }
    },
    [cart]
  );

  const sendSingleItem = useCallback(
    data => {
      const cb = Cart => {
        setUpdatingPosCartStatus(true);
        const requiredCart = [];
        data.forEach(item => {
          if (item.hasLinkedMenuItem) {
            const linkedItems = Cart.filter(
              e =>
                e.isLinked &&
                e.uniqueIdForLinkedItem &&
                e.uniqueIdForLinkedItem === item.uniqueIdForLinkedItem
            );
            requiredCart.push(...linkedItems);
          }
          const Item = Cart.find(i => i.internalId === item.internalId);
          requiredCart.push(Item);
        });
        const dataToSend = {
          storeId: deviceInfo?.storeId,
          employeeId: Number(userInfo?.id),
          deviceId: deviceInfo?.deviceId,
          checkHeaderId: props?.check.id,
          employeeCard: userInfo?.employeeCard,
          items: getLinkedMenuItemsId({
            data: requiredCart,
            allMenusHashTable,
            menuItemsHashTable,
            cart,
          }),
        };
        singleSend(dataToSend)
          .then(res => {
            setUpdatingPosCartStatus(false);
            requiredCart.forEach(i => {
              removeItemFromCart(i.internalId);
            });
            const updatedCart = getUpdatedCart();
            CustomEventEmitter.dispatchEvent('getCartItems', updatedCart);
            updateChecksAfterResponse(res);
            if (
              res.sourceType === 0 &&
              auth?.gamingSystemConfig &&
              activateGameCards(res, menuItemsHashTable)
            ) {
              navigate(`${APP_CONFIG.BASE_URL}/gamecard/${props?.check?.id}`, {
                replace: true,
              });
            }
          })
          .catch(err => {
            setUpdatingPosCartStatus(false);
            setErrorPopUp({
              res: err.message,
            });
          });
      };
      addInProcessItemsToCart(cb, cart);
    },
    [props?.check, userInfo, cart]
  );

  const checkRequiredModifierCount = (menuItemId, menuId) => {
    const requiredModifierList = [];
    menuItemsHashTable[menuItemId]?.modifierGroupLinks.forEach(item => {
      if (
        modifierGroupsHashTable[item.modifierGroupId]?.minimum > 0 &&
        modifierGroupsHashTable[item.modifierGroupId]?.hiddenMenuId !== menuId
      ) {
        requiredModifierList.push({
          ...item,
        });
      }
      if (modifierGroupsHashTable[item.modifierGroupId]?.flowRequired) {
        requiredModifierList.push({
          ...item,
        });
      }
    });
    return requiredModifierList.length;
  };

  const handleRefill = data => {
    const checkItems = props?.check?.orders[0]?.items || [];
    const isItemPresent =
      checkItems.length > 0
        ? data.every(d => checkItems.some(item => item.checkItemId === d.id))
        : false;
    if (!isItemPresent) return;

    const { menuTypeId, menuItemId } = data[0];
    if (
      getObjectValue(
        () => menuCard[menuTypeId][menuItemId]?.menuItem?.linkType,
        0
      ) === 1
    ) {
      navigate(
        `${APP_CONFIG.BASE_URL}/order-item/${menuTypeId}/${menuItemId}?menuId=${menusList[menuIndex]?.id}&parentMenuItemId=${menuItemId}`
      );
    } else {
      const requiredModLength = checkRequiredModifierCount(
        menuItemId,
        menusList[menuIndex].id
      );
      if (requiredModLength === 0) {
        handleRepeatData({ data, isRefill: true });
      } else {
        const modifierList = menuItemsHashTable[
          menuItemId
        ].modifierGroupLinks.map(item => ({
          ...item,
          ...modifierGroupsHashTable[item.modifierGroupId],
        }));
        pushModifiersGroupData(modifierList).then(() => {
          navigate(
            `${
APP_CONFIG.BASE_URL
            }/order-item/${menuTypeId}/${menuItemId}?menuId=${
menusList[menuIndex]?.id
            }&orderType=order&refill=${true}`
          );
        });
      }
    }
  };

  const activeCartButtonHandlers = {
    move: data => setIsMoveModalOpen({ isOpen: true, data: [data] }),
    repeat: data => handleRepeatData({ data: [data] }),
    edit: data => onModifyItem(data, 'edit'),
    // delete: (data) => deleteItem([data]),
    delete: data =>
      setIsActionModalOpen({ isOpen: true, data: [data], type: DELETE }),
    send: data =>
      setIsActionModalOpen({ isOpen: true, data: [data], type: SEND }),
    refill: data => handleRefill([data]),
    void: data => handleManagerAuthentication([data]),
  };

  const actionConfirmationModal = useMemo(() => {
    const isOpen = isActionModalOpen?.isOpen;
    const isDelete = isActionModalOpen?.type === DELETE;
    const isSend = isActionModalOpen?.type === SEND;
    return {
      isOpen,
      data: isActionModalOpen?.data,
      customMessage: isDelete
        ? CUSTOM_MESSAGES().DELETE_ITEM_CONFIRMATION
        : CUSTOM_MESSAGES().SINGLE_SEND_MSG,
      closeModal: () => setIsActionModalOpen(defaultActionState),
      handleSubmit: () => {
        if (isDelete) {
          Promise.resolve(deleteItem(isActionModalOpen?.data))
            .then(() => {
              setIsActionModalOpen(defaultActionState);
            })
            .catch(() => {
              setIsActionModalOpen(defaultActionState);
            });
        } else if (isSend) {
          sendSingleItem(isActionModalOpen?.data);
          setIsActionModalOpen(defaultActionState);
        }
      },
    };
  }, [isActionModalOpen]);

  const buttonVisibility = getButtonVisibility();

  const buttonVisibilityManager = {
    move: () => buttonVisibility?.canMove,
    repeat: data =>
      (data?.isDiscount || data?.discountId || (data?.discountName && !data?.menuItemId) || data?.isServiceCharge || data?.serviceChargeId || (data?.serviceChargeName && !data?.menuItemId))
        ? false
        : props.isPendingList
          ? buttonVisibility?.canRepeat
          : repeatButtonVisibilityCheck([data]),

    delete: () => buttonVisibility?.canDelete,
    edit: () => buttonVisibility?.canEdit,
    send: () => buttonVisibility?.canSend,

    refill: data => (data?.isDiscount || data?.isServiceCharge ? false : refillButtonVisibilityCheck(data)),
    void: () => itemVoidButtonVisibilityCheck(activeTab),
  };

  const isEditDisabledItem = item =>
    (menuItemsHashTable?.[item?.menuItemId]?.minOrderQuantity > 0) ||
    menuItemsHashTable?.[item?.menuItemId]?.cardType === 1 ||
    menuItemsHashTable?.[item?.menuItemId]?.cardType === 2;

  const getPackLinkedItems = item => {
    const uid =
      item?.uniqueIdForGameCardItem ||
      safeJsonParse(item?.modifierJson || {})?.uniqueIdForGameCardItem;
    if (!uid) return [item];
    const linked = cart.filter(c => {
      const cUid =
        c?.uniqueIdForGameCardItem ||
        safeJsonParse(c?.modifierJson || {})?.uniqueIdForGameCardItem;
      return cUid === uid;
    });
    return linked.length > 0 ? linked : [item];
  };

  const getRefillItemName = useCallback(
    item => {
      const refillMenuItemId =
        menuItemsHashTable?.[item?.menuItemId]?.refillMenuItemId;
      const refillItem = menuItemsHashTable?.[refillMenuItemId];
      return refillItem?.name || item?.menuItemName;
    },
    [menuItemsHashTable]
  );

  const hasCheckedDiscount = useMemo(() => {
    if (props.isPendingList || !hasAnyCheckedItem) return false;
    return activeList
      .filter(item => selectedItems[getSelectorId(item)])
      .some(
        item =>
          item?.isDiscount ||
          item?.discountId ||
          (item?.discountName && !item?.menuItemId) ||
          item?.isServiceCharge ||
          item?.serviceChargeId ||
          (item?.serviceChargeName && !item?.menuItemId)
      );
  }, [activeList, selectedItems, props.isPendingList, hasAnyCheckedItem]);

  const showVoid = itemVoidButtonVisibilityCheck(activeTab);
  const buttonsToShowCount = useMemo(() => {
    if (!hasAnyCheckedItem) return 0;
    if (hasCheckedDiscount) {
      return auth?.nonIntegratedPos ? 2 : 1;
    }
    if (hasCheckedPackItem) {
      return props.isPendingList ? 1 : 0;
    }
    const isStandardMode = !isTogo && !isCounter && !isCallCenter && !isSpeedOrderMode && !isDriveThru;
    if (!isStandardMode) return 0;

    if (props.isPendingList) {
      return 5 + (enableItemHold ? 1 : 0);
    } else {
      return 2 + (showVoid ? 1 : 0);
    }
  }, [hasAnyCheckedItem, hasCheckedDiscount, hasCheckedPackItem, props.isPendingList, isTogo, isCounter, isCallCenter, isSpeedOrderMode, isDriveThru, auth?.nonIntegratedPos, enableItemHold]);

  return (
    <div
      className={cn(
        hasAnyCheckedItem &&
          !isTogo &&
          !isCounter &&
          !isCallCenter &&
          !isSpeedOrderMode &&
          !isDriveThru
          ? (hasCheckedPackItem
            ? 'h-66'
            : props.isPendingList
              ? (buttonsToShowCount > 5 ? 'h-46 pb-3' : 'h-56 pb-3')
              : buttonsToShowCount > 2
                ? 'h-56 pb-3'
                : 'h-66 pb-2'
          )
          : isTogo || isCallCenter || isDriveThru
            ? 'h-74'
            : isCounter
              ? 'h-84'
              : isSpeedOrderMode
                ? 'h-77'
                : 'h-66 pb-2'
      )}
    >
      {!isTogo &&
        !isCounter &&
        !isCallCenter &&
        !isSpeedOrderMode &&
        !isDriveThru && (
          <CartItemsTab
            newItemsStyle={newItemsStyle}
            sentCount={sentItems.length}
            pendingCount={pendingItems.length}
            handleSelectAll={handleSelectAll}
            isAllSelected={
              activeList.length > 0 &&
              activeList.every(item => selectedItems[getSelectorId(item)])
            }
          />
        )}
      <div
        className={cn('d-flex pt-1 fs_12 mb-2')}
        style={{ ...newItemsStyle.qtyStyle, height: '24px', minHeight: '24px' }}
      >
        <div className={cn('d-flex col-12 px-0')}>
          <div className="col-1 p-0"></div>
          <div className={cn('col-1 px-0 pl-0')}>Qty</div>
          {isCounter || isCallCenter || isSpeedOrderMode || isDriveThru ? (
            <>
              <div className="col-7 px-2">Items</div>
              <div className={cn('col-3 pl-1')}>
                {LABEL().CURRENCY_SYMBOL}Amt
              </div>
            </>
          ) : (
            <>
              <div className="col-5 px-2">Items</div>
              <div className={cn('col-2 px-0 text-center')}>Seat</div>
              <div className={cn('col-3 px-0 pl-1')}>
                {activeTab === 'sent' ? (
                  <>$Amt</>
                ) : (
                  <>{LABEL().CURRENCY_SYMBOL}Amt</>
                )}
              </div>
            </>
          )}
        </div>
      </div>
      {!props.isCartLoading ? (
        <div
          className={cn(
            isSpeedOrderMode
              ? styles.cartItemsListScroller
              : styles.cartItemsList_scroller,
            styles.cartItemsList_scroller,
            isCounter ||
              isCallCenter ||
              isTogo ||
              isSpeedOrderMode ||
              isDriveThru
              ? 'h-91'
              : hasAnyCheckedItem &&
                !hasCheckedPackItem &&
                props.isPendingList &&
                buttonsToShowCount > 5
                ? 'h-75'
                : 'h-80'
          )}
        >
          {displayList.length === 0 ? (
            <div
              className="d-flex justify-content-center align-items-center h-100 fs_16 fw-600"
              style={{ color: serverAppTheme?.noRecord }}
            >
              {CUSTOM_MESSAGES().NO_ITEM_FOUND}
            </div>
          ) : (
            displayList.map((item) => {
              const itemSendDate = item.sendDate ?? sendDateMap[item.id];
              const itemMessage = item.message ?? messageMap[item.id];
              const uniqueId = item.id ?? item.internalId;
              const isLapsed = lapsedHolds[uniqueId] ?? (itemSendDate ? !getHoldTimerState(itemSendDate)?.isPositive : false);

              return (
                <div
                  key={uniqueId}
                  style={{
                    backgroundColor:
                      item.sent || item?.discountId || item?.isDiscount || item?.isServiceCharge || item?.serviceChargeId
                        ? serverAppTheme?.send_tab_unselected
                        : itemSendDate
                          ? (isLapsed ? 'rgb(245, 176, 176)' : 'rgb(255, 225, 204)')
                          : serverAppTheme?.pending_items,
                  }}
                  onClick={() => {
                    setSelectedItems({});
                    setActiveCartItem(prev =>
                      prev === getSelectorId(item) ? null : getSelectorId(item)
                    );
                  }}
                  className={cn('mb-1')}
                >
                  <div className={cn('d-flex  px-0 py-2 fw-500 fs-14')}>
                    <div className={cn("col-1 p-0 ml-1", styles.paddingTop)}>
                      <div
                        className={cn(
                          'd-flex justify-content-center align-items-center',
                          styles.checkBoxStyle
                        )}
                      >
                        <input
                          type="checkbox"
                          name={`checkbox-{item.id}`}
                          checked={!!selectedItems[getSelectorId(item)]}
                          className={cn(styles.checkbox)}
                          style={newItemsStyle.itemsStyle}
                          onChange={() => handleSelectOne(item)}
                          onClick={e => {
                            e.stopPropagation();
                            setActiveCartItem(null);
                          }}
                        />
                      </div>
                    </div>
                    <div className={cn('col-1 px-0 pl-0 text-center')} style={{ color: 'black' }}>
                      {(() => {
                        const explicitSplit = item?.seats?.[0]?.splitValue;
                        const rawInferredSplit = Array.isArray(item?.seatIds) ? item.seatIds.length : 0;
                        const inferredSplit = rawInferredSplit > 0 ? rawInferredSplit : openSeatsCount;
                        const splitValue = explicitSplit > 1 ? explicitSplit : (inferredSplit > 1 ? inferredSplit : 1);
                        return splitValue > 1 && props.position?.number !== -1 && item.quantity
                          ? `${item.quantity}/${splitValue}`
                          : item.quantity;
                      })()}
                    </div>
                    {isCounter || isSpeedOrderMode || isDriveThru ? (
                      <>
                        <div className={cn('col-7 pl-1 text-left')}>
                          {item?.refill ? getRefillItemName(item) : item.menuItemName}
                        </div>
                        <div className={cn('col-3 px-0')}>
                          {(item.sent || displayPrice) && (
                            <>${(item.refill ? 0 : (item.price ?? 0))?.toFixed(2)}</>
                          )}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className={cn('col-5 px-0 pl-1 text-left text-break')} style={{color: 'black'}}>
                          {item?.refill ? getRefillItemName(item) : item.menuItemName}
                        </div>
                        <div className={cn('col-2 px-0 d-flex justify-content-center', styles.paddingTop)}>
                          <div className={styles.seatbox} style={{
                            background: serverAppTheme?.seat_bg,
                            color: serverAppTheme?.seat_box_text,
                          }}>
                            {(() => {
                              const seatIds = Array.isArray(item?.seatIds) && item?.seatIds.length > 0
                                ? item.seatIds
                                : (item?.seats ?? []).map(s => s?.seatId).filter(Boolean);
                              if (seatIds.length > 1) return 'S';
                              if (seatIds.length === 1) {
                                const found = props.check?.seats?.find(i => i?.id === seatIds[0]);
                                if (found?.number != null) return found.number;
                              }
                              if (props.position?.number != null && props.position?.number !== -1) {
                                return props.position.number;
                              }
                              return (item?.isDiscount || item?.discountId || item?.isServiceCharge || item?.serviceChargeId) ? '' : 'T';
                            })()}{' '}
                          </div>
                        </div>
                        <div className={cn('col-3 px-0')} style={{ color: 'black' }}>
                          {(item.sent || item?.discountId || item?.isDiscount || item?.isServiceCharge || item?.serviceChargeId || displayPrice) && (
                            <span>{(() => {
                              if (item.refill) return `$${(0).toFixed(2)}`;
                              if (item.isDiscount || item?.discountId) {
                                const discountVal = item.price ?? item.value ?? 0;
                                const absVal = Math.abs(Number(discountVal) || 0);
                                return `-$${absVal.toFixed(2)}`;
                              }
                              if (item.isServiceCharge || item?.serviceChargeId) {
                                const scVal = item.price ?? item.amount ?? item.serviceChargeAmount ?? item.value ?? 0;
                                const absVal = Math.abs(Number(scVal) || 0);
                                return `$${absVal.toFixed(2)}`;
                              }
                              const explicitSplit = item?.seats?.[0]?.splitValue;
                              const rawInferredSplit = Array.isArray(item?.seatIds) ? item.seatIds.length : 0;
                              const inferredSplit = rawInferredSplit > 0 ? rawInferredSplit : openSeatsCount;
                              const splitValue = explicitSplit > 1 ? explicitSplit : (inferredSplit > 1 ? inferredSplit : 1);
                              const numVal = splitValue > 1 && props.position?.number !== -1
                                ? (Number(item.price ?? 0) / splitValue)
                                : Number(item.price ?? 0);
                              return `$${numVal.toFixed(2)}`;
                            })()}</span>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                  {item?.plannedCompleteDate !== null &&
                    (item?.kdsStatus === 2 ||
                      item?.kdsStatus === 5 ||
                      item?.kdsStatus === 8) && (
                      <div className="col-4 text-center px-0">
                        <img
                          src={displayKdsStatus(item?.kdsStatus)}
                          alt="frying-pan"
                          width="30px"
                        />
                        <div className="fs-12">
                          {getLocalTime(item?.plannedCompleteDate)}
                        </div>
                      </div>
                    )}
                  {/* {activeCartItem === getSelectorId(item) && (
                    <div
                      className="d-flex flex-wrap px-2 pb-2 my-1"
                      style={{
                        gap: '2px',
                      }}
                    >
                      {activeCartButton.map(button => {
                        return buttonVisibilityManager[button.key](item) ? (
                          <TouchableButton
                            key={button.key}
                            type="button"
                            className={cn(
                              styles.borderNone,
                              'text-white text-center d-flex justify-content-center align-items-center'
                            )}
                            style={{
                              flexBasis: 'calc(50% - 2px)',
                              background: button.backgroundColor,
                              width: '100%',
                              height: '24px',
                              fontSize: '14px',
                              fontWeight: 500,
                              padding: 0,
                            }}
                            rippleEffect={true}
                            onClick={e => {
                              e.stopPropagation();
                              if (activeCartButtonHandlers[button.key]) {
                                activeCartButtonHandlers[button.key](item);
                              }
                            }}
                          >
                            {button.text}
                          </TouchableButton>
                        ) : (
                          <></>
                        );
                      })}
                    </div>
                  )} */}
                  {item.refill && (
                    <div
                      className="pr-0 pl-1 pb-1 col-1 text-center"
                      role="presentation"
                    >
                      <RefillIcon
                        fill={serverAppTheme?.iconcolor}
                        alt="refill"
                      />
                    </div>
                  )}
                  {(item.note || item.comment) && (
                    <div
                      style={noteColor}
                      className={cn(
                        'col-12 row mx-0 pl-1 pb-2',
                        styles.cmntFont
                      )}
                    >
                      <span className="col-1 px-0">
                        <NotesIcon />
                      </span>
                      <span
                        className={cn('pl-2 col-11 pr-0', styles.breakWord)}
                      >
                        {item.note || item.comment}
                      </span>
                    </div>
                  )}
                  {(() => {
                    const parsedModJson = safeJsonParse(item.modifierJson, {});
                    const modifiersToShow = item.isLinked
                      ? []
                      : parsedModJson.hasLinkedMenuItem
                        ? parsedModJson.modifiers
                        : item.modifiers;
                    return modifiersToShow && modifiersToShow.length > 0 && (
                      <div className={cn('col-11 px-0 pl-1')}>
                        <ModifiersList
                          modifiersObj={modifiersToShow}
                          nest={false}
                          serverAppTheme={serverAppTheme}
                          displayPrice={!item.sent && displayPrice}
                        />
                      </div>
                    );
                  })()}
                  {itemSendDate ? (
                    <HoldTimerBadge
                      sendDate={itemSendDate}
                      serverAppTheme={serverAppTheme}
                      onFlip={(isPositive) => handleHoldFlip(uniqueId, isPositive)}
                    />
                  ) : null}
                  {itemSendDate && (
                    <HoldExpiredMessage
                      sendDate={itemSendDate}
                      message={itemMessage}
                      className={styles.holdTimerBadge}
                    />
                  )}
                  {activeCartItem === getSelectorId(item) && (
                    <div
                      className={cn(
                        'd-flex flex-wrap px-1 my-1 justify-content-between',
                      )}
                    >
                      {/* v2 order screen */}
                      {activeCartButton
                        .filter(button => {
                          if (isEditDisabledItem(item) && button.key !== 'delete') return false;
                          return buttonVisibilityManager[button.key](item);
                        })
                        .map(button => (
                          <TouchableButton
                            key={button.key}
                            type="button"
                            className={cn(
                              styles.borderNone,
                              'text-white text-center d-flex justify-content-center align-items-center pl-1 mb-1'
                            )}
                            style={{
                              flexBasis: 'calc(50% - 2px)',
                              background: button.backgroundColor,
                              width: '100%',
                              height: '24px',
                              fontSize: '14px',
                              fontWeight: 500,
                              padding: 0,
                            }}
                            rippleEffect={true}
                            onClick={e => {
                              e.stopPropagation();
                              if (button.key === 'delete' && isEditDisabledItem(item)) {
                                setIsActionModalOpen({ isOpen: true, data: getPackLinkedItems(item), type: DELETE });
                              } else if (activeCartButtonHandlers[button.key]) {
                                activeCartButtonHandlers[button.key](item);
                              }
                            }}
                          >
                            {button.text}
                          </TouchableButton>
                        ))}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      ) : (
        <CartItemsSkeleton />
      )}
      {actionConfirmationModal.isOpen && (
        <ActionConfirmationModal
          isOpen={actionConfirmationModal.isOpen}
          onClose={actionConfirmationModal.closeModal}
          data={actionConfirmationModal.data}
          customMessage={actionConfirmationModal.customMessage}
          handleSubmit={actionConfirmationModal.handleSubmit}
        />
      )}
      {isMoveModalOpen.isOpen && (
        <MoveItemModal
          isOpen={isMoveModalOpen?.isOpen}
          data={isMoveModalOpen?.data}
          closeModal={() => setIsMoveModalOpen({ isOpen: false, data: [] })}
        />
      )}
      {updatingCartStatus && (
        <Loader message="Updating Item" serverAppTheme={serverAppTheme} />
      )}
      {updatingPosCartStatus && (
        <Loader
          message="Waiting for response from POS"
          serverAppTheme={serverAppTheme}
        />
      )}
    </div>
  );
};

const mapStateToProps = ({ orders, theme, oauth }) => ({
  position: orders.position,
  check: orders.seats,
  serverAppTheme: theme.serverAppTheme,
  cart: orders.cart,
  menuItemsHashTable: orders.menuItemsHashTable,
  isPendingList: orders.isPendingList,
  isCartLoading: orders.isCartLoading,
  deviceInfo: oauth.deviceInformation,
  userInfo: oauth.loggedInUserData,
  allMenusHashTable: orders.allMenusHashTable,
  auth: oauth.authInfo,
  menusList: orders.menusList,
  menuCard: orders.menuCardList,
  menuIndex: orders.menuIndex,
  modifierGroupsHashTable: orders.modifierGroupsHashTable,
  updatingCartStatus: orders.updatingCartStatus,
  updatingPosCartStatus: orders.updatingPosCartStatus
});

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      changeCheckOrdersTab,
      deleteItm,
      updateChecksAfterResponse,
      updateSeatInfo,
      replaceCart,
      singleSend,
      removeItemFromCart,
      setErrorPopUp,
      setHighlightCartItems,
      pushModifiersGroupData,
      setUpdatingPosCartStatus,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(memo(CartItems));
